import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Share2, Copy, CheckCircle, Users, Gift, Mail } from 'lucide-react';
import { useSettings } from '@/hooks/useProducts';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import Button from '@/components/ui/Button';

interface ReferralReward {
  id: string;
  side: 'referrer' | 'referee';
  reward_type: 'points' | 'percentage' | 'fixed' | 'free_month';
  reward_value: number;
  reward_duration: number;
  status: 'issued' | 'redeemed' | 'expired' | 'failed';
  promotion_code: string | null;
  created_at: string;
}

export default function Referrals() {
  const { t } = useTranslation('account');
  const { data: settingsData } = useSettings();
  const config = settingsData?.settings?.upsell_config?.referral_program;
  const [copied, setCopied] = useState(false);
  const [copiedRewardId, setCopiedRewardId] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<{ ok: boolean; msg: string } | null>(null);

  const { data: codeData } = useQuery({
    queryKey: ['referral-code'],
    queryFn: () =>
      api.get<{ code: string }>('/referrals/my-code'),
    enabled: !!config?.enabled,
  });

  const { data: statsData } = useQuery({
    queryKey: ['referral-stats'],
    queryFn: () =>
      api.get<{ total_referrals: number; completed: number; pending: number }>('/referrals/stats'),
    enabled: !!config?.enabled,
  });

  const { data: rewardsData } = useQuery({
    queryKey: ['referral-rewards'],
    queryFn: () =>
      api.get<{ rewards: ReferralReward[] }>('/referrals/my-rewards'),
    enabled: !!config?.enabled,
  });

  const code = codeData?.code;
  const stats = statsData || { total_referrals: 0, completed: 0, pending: 0 };
  const rewards = rewardsData?.rewards ?? [];
  const frontendUrl = window.location.origin;
  const referralUrl = code ? `${frontendUrl}/signup?ref=${code}` : '';

  function describeReward(r: ReferralReward): string {
    switch (r.reward_type) {
      case 'free_month':
        return r.reward_duration > 1
          ? t('referrals.freeMonthMany', { count: r.reward_duration })
          : t('referrals.freeMonthOne');
      case 'percentage':
        return r.reward_duration > 1
          ? t('referrals.percentMany', { value: r.reward_value, count: r.reward_duration })
          : t('referrals.percentOnce', { value: r.reward_value });
      case 'fixed':
        return r.reward_duration > 1
          ? t('referrals.fixedMany', { value: r.reward_value, count: r.reward_duration })
          : t('referrals.fixedOnce', { value: r.reward_value });
      case 'points':
        return t('referrals.pointsReward', { value: r.reward_value });
    }
  }

  // Reward-description copy adapts to the configured reward shape so admins
  // can swap free_month → percentage without a string change here.
  function previewLabel(side: 'referrer' | 'referee'): string {
    const type = config?.[`${side}_reward_type`];
    const value = config?.[`${side}_reward_value`];
    const duration = config?.[`${side}_reward_duration`] ?? 1;
    if (type === 'free_month')
      return duration > 1
        ? t('referrals.freeMonthMany', { count: duration })
        : t('referrals.freeMonthOne');
    if (type === 'percentage')
      return duration > 1
        ? t('referrals.previewPercentCycles', { value, count: duration })
        : t('referrals.percentOnce', { value });
    if (type === 'fixed')
      return duration > 1
        ? t('referrals.previewFixedCycles', { value, count: duration })
        : t('referrals.fixedOnce', { value });
    if (type === 'points') return t('referrals.pointsReward', { value });
    return '';
  }

  function rewardStatusLabel(status: ReferralReward['status']): string {
    switch (status) {
      case 'issued': return t('referrals.statusReady');
      case 'redeemed': return t('referrals.statusRedeemed');
      case 'expired': return t('referrals.statusExpired');
      case 'failed': return t('referrals.statusFailed');
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleCopyReward(rewardId: string, value: string) {
    navigator.clipboard.writeText(value);
    setCopiedRewardId(rewardId);
    setTimeout(() => setCopiedRewardId(null), 2000);
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteEmail || inviteSending) return;
    setInviteSending(true);
    setInviteStatus(null);
    try {
      await api.post('/referrals/invite', { email: inviteEmail });
      setInviteStatus({ ok: true, msg: t('referrals.inviteSent') });
      setInviteEmail('');
    } catch {
      setInviteStatus({ ok: false, msg: t('referrals.inviteFailed') });
    } finally {
      setInviteSending(false);
    }
  }

  if (!config?.enabled) {
    return (
      <div className="text-center py-16">
        <Share2 className="h-16 w-16 text-ink-muted mx-auto mb-4" aria-hidden="true" />
        <h1 className="text-2xl font-bold text-ink">{t('referrals.unavailableHeading')}</h1>
        <p className="mt-2 text-ink-muted">{t('referrals.unavailable')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-display font-bold text-ink">{t('referrals.heading')}</h1>

      {/* Rewards summary */}
      <div className="bg-gradient-to-r from-sage-700 to-sage-800 rounded-xl p-6 text-white">
        <h2 className="text-lg font-semibold mb-2">{t('referrals.rewardsHeading')}</h2>
        <p className="text-sage-200 text-sm">
          {t('referrals.rewardsBody', {
            referrerPreview: previewLabel('referrer'),
            refereePreview: previewLabel('referee'),
          })}
        </p>
      </div>

      {/* Referral link */}
      {code && (
        <div className="bg-surface rounded-lg border border-line p-6">
          <h2 className="font-semibold text-ink mb-3">{t('referrals.linkHeading')}</h2>
          <div className="flex gap-2">
            <input
              readOnly
              value={referralUrl}
              className="flex-1 px-3 py-2 bg-surface-soft border border-line-strong rounded-lg text-sm text-ink-soft"
            />
            <Button variant="outline" onClick={handleCopy}>
              {copied ? <CheckCircle className="h-4 w-4 text-success-600" /> : <Copy className="h-4 w-4" />}
              {copied ? t('referrals.copied') : t('referrals.copy')}
            </Button>
          </div>
          <p className="mt-2 text-xs text-ink-muted">
            {t('referrals.yourCode')} <span className="font-mono font-bold">{code}</span>
          </p>
        </div>
      )}

      {/* Invite a friend by email */}
      {code && (
        <div className="bg-surface rounded-lg border border-line p-6">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="h-5 w-5 text-sage-700" aria-hidden="true" />
            <h2 className="font-semibold text-ink">{t('referrals.inviteHeading')}</h2>
          </div>
          <form className="flex gap-2" onSubmit={handleInvite}>
            <input
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={t('referrals.invitePlaceholder')}
              className="flex-1 px-3 py-2 bg-surface-soft border border-line-strong rounded-lg text-sm text-ink-soft"
            />
            <Button type="submit" disabled={inviteSending || !inviteEmail}>
              {inviteSending ? t('referrals.inviteSending') : t('referrals.inviteSend')}
            </Button>
          </form>
          {inviteStatus && (
            <p className={cn('mt-2 text-sm', inviteStatus.ok ? 'text-success-700' : 'text-red-600')}>
              {inviteStatus.msg}
            </p>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="bg-surface rounded-lg border border-line p-4 text-center">
          <Users className="h-5 w-5 text-ink-muted mx-auto mb-1" aria-hidden="true" />
          <p className="text-2xl font-bold text-ink">{stats.total_referrals}</p>
          <p className="text-sm text-ink-muted">{t('referrals.totalReferrals')}</p>
        </div>
        <div className="bg-surface rounded-lg border border-line p-4 text-center">
          <CheckCircle className="h-5 w-5 text-success-500 mx-auto mb-1" aria-hidden="true" />
          <p className="text-2xl font-bold text-ink">{stats.completed}</p>
          <p className="text-sm text-ink-muted">{t('referrals.completed')}</p>
        </div>
        <div className="bg-surface rounded-lg border border-line p-4 text-center">
          <p className="text-2xl font-bold text-ink">{stats.pending}</p>
          <p className="text-sm text-ink-muted">{t('referrals.pending')}</p>
        </div>
      </div>

      {/* Earned rewards */}
      {rewards.length > 0 && (
        <div className="bg-surface rounded-lg border border-line p-6">
          <div className="flex items-center gap-2 mb-3">
            <Gift className="h-5 w-5 text-sage-700" aria-hidden="true" />
            <h2 className="font-semibold text-ink">{t('referrals.rewardsSectionHeading')}</h2>
          </div>
          <ul className="divide-y divide-line">
            {rewards.map((r) => (
              <li key={r.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-ink">{describeReward(r)}</p>
                  <p className="text-xs text-ink-muted">
                    {r.side === 'referrer' ? t('referrals.sideReferrer') : t('referrals.sideReferee')}
                    {' · '}
                    {rewardStatusLabel(r.status)}
                  </p>
                </div>
                {r.promotion_code && r.status === 'issued' && (
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-surface-muted px-2 py-1 rounded text-ink">
                      {r.promotion_code}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopyReward(r.id, r.promotion_code!)}
                    >
                      {copiedRewardId === r.id ? (
                        <CheckCircle className="h-4 w-4 text-success-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                )}
                {r.reward_type === 'points' && r.status === 'issued' && (
                  <span className="text-sm font-medium text-success-700">
                    {t('referrals.pointsBadge', { value: r.reward_value })}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-ink-muted">{t('referrals.rewardsFooter')}</p>
        </div>
      )}
    </div>
  );
}
